<template>
	<div id="app">
		<v-app>
			<ApplicationLayout
				v-if="showMainLayout && tokenInDate && !sessionExpired"
				:links="links"
				:show-spinner="loadingSpinner"
				:change-show-filters="changeShowFilters"
				:reset-admin-portal-filters="resetAdminPortalFilters"
			>
				<template #header-extra>
					<ProductSwitchButton />
				</template>
				<RouterView :key="$route.fullPath" />
			</ApplicationLayout>
			<RouterView
				v-else-if="!sessionExpired"
				:key="$route.fullPath"
			/>
			<v-snackbar
				:value="!!snackbar"
				top
				:color="snackbar ? snackbar.color : ''"
				@input="snackbar = null"
			>
				{{ snackbar ? snackbar.text : '' }}
				<template #action="{ attrs }">
					<v-btn
						color="white"
						text
						v-bind="attrs"
						@click="snackbar = null"
					>
						Close
					</v-btn>
				</template>
			</v-snackbar>
			<UnsavedChangesModal />
			<SessionExpiryModal
				v-if="sessionWarning"
				title="Session Expiry Warning"
				:countdown="countdown"
				:keep-logged-in="renewSession"
				:log-out="logOut"
			/>
		</v-app>
	</div>
</template>

<script>
/* eslint-disable max-lines */
import { mapActions, mapGetters } from 'vuex'
import '../../shared/styles/shared-styles.css'
import appConfig from '../src/app.config.json'
import ApplicationLayout from '../../shared/layouts/application-layout.vue'
import ProductSwitchButton from '../../shared/components/product-switch-button.vue'
import { navBarItems, DASHBOARD_NAV_CUSTOM_ACTION, PRIVACY_NOTICE_NAV_CUSTOM_ACTION, PREFPAGE_ANALYTICS_NAV_CUSTOM_ACTION, CONSENT_CONFIGURATION_API_NAV_CUSTOM_ACTION } from '../../shared/state/admin-portal-navigation.js'
import { zohoNewTicketLink, zohoViewTicketLink, learningHubLink } from '../../shared/state/config-keys.js'
import { snackbar } from '../../shared/state/snackbar.js'
import { loadingSpinner } from '../../shared/state/loading-spinner.js'
import { changeShowFilters } from '../../shared/state/filters.js'
import { resetAdminPortalFilters } from '../../shared/utils/filters.js'
import UnsavedChangesModal from '../../shared/components/unsaved-changes-modal.vue'
import SessionExpiryModal from '../../shared/components/session-expiry-modal.vue'
import { navigateToDashboardPortal, NAV_MODE_EMBEDDED } from '../../shared/utils/navigation/dashboard-navigation.js'
import { navigateToPrivacyNoticePortal } from '../../shared/utils/navigation/privacy-notice-navigation.js'
import { navigateToPrefPageAnalytics } from '../../shared/utils/navigation/prefpage-analytics-navigation.js'
import { navigateToConsentConfigurationApiPortal } from '../../shared/utils/navigation/consent-configuration-api-navigation.js'

export default {
	components: { UnsavedChangesModal, ApplicationLayout, SessionExpiryModal, ProductSwitchButton },
	provide () {
		return {
			logOutAndRedirect: this.logOut,
			userFullName: () => this.userFullName
		}
	},
	setup () {
		return {
			loadingSpinner,
			snackbar,
			zohoNewTicketLink,
			zohoViewTicketLink,
			learningHubLink,
			changeShowFilters,
			resetAdminPortalFilters
		}
	},
	data () {
		return {
			sessionExpired: false
		}
	},
	computed: {
		...mapGetters('auth', ['userFullName', 'migrationUrl', 'productAreaPermission', 'tokenInDate', 'sessionWarning', 'countdown']),
		showMainLayout () {
			return this.$route.meta?.authRequired && !this.$route.meta?.hideLayout
		},
		links () {
			if (navBarItems?.value == null) {
				return null
			}
			return navBarItems.value.map(topLevelNavItem => ({
				...topLevelNavItem,
				customAction: this.resolveCustomAction(topLevelNavItem, this.getParentLevelCustomAction),
				sublinks: topLevelNavItem.sublinks
					? topLevelNavItem.sublinks.map(subLinkNavItem => ({
						...subLinkNavItem,
						customAction: this.resolveCustomAction(subLinkNavItem, this.getChildLevelCustomAction),
						deeperSublinks: subLinkNavItem.deeperSublinks
							? subLinkNavItem.deeperSublinks.map(deeperSublinkItem => ({
								...deeperSublinkItem,
								customAction: this.resolveCustomAction(deeperSublinkItem, this.getGrandchildLevelCustomAction)
							}))
							: null
					}))
					: null
			}))
		}
	},
	watch: {
		sessionWarning (val) {
			if (!val) {
				this.sessionExpired = false
			}
		}
	},
	page: {
		titleTemplate (title) {
			title = typeof title === 'function' ? title(this.$store) : title
			return title ? `${title} | ${appConfig.title}` : appConfig.title
		}
	},
	mounted () {
		const link = document.createElement('link')
		link.type = 'image/svg+xml'
		link.rel = 'shortcut icon'
		link.href = require('../src/assets/favicon.svg')
		document.getElementsByTagName('head')[0].appendChild(link)
	},
	methods: {
		...mapActions('auth', ['logOut', 'getLegacyUrl', 'validate']),
		async goToExternalUrl (route, text) {
			if (text === 'Create Support Ticket' && zohoNewTicketLink.value != null) {
				window.open(zohoNewTicketLink.value)
			} else if (text === 'View Support Ticket' && zohoViewTicketLink.value != null) {
				window.open(zohoViewTicketLink.value)
			} else if (text === 'Learning HUB' && learningHubLink.value != null) {
				window.open(learningHubLink.value, '_blank')
			} else {
				if (this.migrationUrl == null) {
					const migrationUrl = await this.getLegacyUrl()
					if (window.location.hostname === 'localhost' || (migrationUrl == null)) {
						return null
					}
				}
				if (this.migrationUrl != null) {
					location.href = `http://${this.migrationUrl[1]}/${route}`
				}
			}
		},
		resolveCustomAction (navItem, fallbackResolver) {
			if (navItem?.customActionType) {
				return this.getCustomActionByType(navItem.customActionType)
			}
			if (typeof navItem?.customAction === 'function') {
				return navItem.customAction
			}
			return typeof fallbackResolver === 'function' ? fallbackResolver(navItem) : null
		},
		getCustomActionByType (type) {
			switch (type) {
				case DASHBOARD_NAV_CUSTOM_ACTION:
					return async () => {
						// Entered from the admin-portal navbar -> embedded: the
						// consent-banners app keeps the admin navbar (AB#25457).
						await navigateToDashboardPortal(NAV_MODE_EMBEDDED)
					}
				case PRIVACY_NOTICE_NAV_CUSTOM_ACTION:
					return async () => {
						await navigateToPrivacyNoticePortal()
					}
				case PREFPAGE_ANALYTICS_NAV_CUSTOM_ACTION:
					return async () => {
						await navigateToPrefPageAnalytics()
					}
				case CONSENT_CONFIGURATION_API_NAV_CUSTOM_ACTION:
					return async () => {
						await navigateToConsentConfigurationApiPortal()
					}
				default:
					return null
			}
		},
		getParentLevelCustomAction (parentLevelNavItem) {
			return parentLevelNavItem?.isExternal ? () => this.goToExternalUrl(parentLevelNavItem.route, parentLevelNavItem.text) : null
		},
		getChildLevelCustomAction (childLevelNavItem) {
			return childLevelNavItem?.isExternal ? () => this.goToExternalUrl(childLevelNavItem.route, childLevelNavItem.text) : null
		},
		getGrandchildLevelCustomAction (grandchildLevelNavItem) {
			return grandchildLevelNavItem?.isExternal ? () => this.goToExternalUrl(grandchildLevelNavItem.route, grandchildLevelNavItem.text) : null
		},
		renewSession () {
			this.validate().then(() => {
				this.$store.commit('auth/SET_SESSION_WARNING', false)
			})
		}
	}
}
</script>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
//
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

@import './styles';
// Mont font - Optimized loading with essential weights only
// Loading only the most commonly used font weights to improve performance
@font-face {
  font-family: 'Mont';
  src: url('./fonts/Mont-Regular.otf') format('opentype');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Mont';
  src: url('./fonts/Mont-Bold.otf') format('opentype');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}

// Inter font - Complete font family
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Thin.woff2') format('woff2');
  font-weight: 100;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-ExtraLight.woff2') format('woff2');
  font-weight: 200;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Light.woff2') format('woff2');
  font-weight: 300;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Regular.woff2') format('woff2');
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Medium.woff2') format('woff2');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-SemiBold.woff2') format('woff2');
  font-weight: 600;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Bold.woff2') format('woff2');
  font-weight: 700;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-ExtraBold.woff2') format('woff2');
  font-weight: 800;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Black.woff2') format('woff2');
  font-weight: 900;
  font-style: normal;
  font-display: swap;
}

// Inter Italic variants
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-ThinItalic.woff2') format('woff2');
  font-weight: 100;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-ExtraLightItalic.woff2') format('woff2');
  font-weight: 200;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-LightItalic.woff2') format('woff2');
  font-weight: 300;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-Italic.woff2') format('woff2');
  font-weight: 400;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-MediumItalic.woff2') format('woff2');
  font-weight: 500;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-SemiBoldItalic.woff2') format('woff2');
  font-weight: 600;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-BoldItalic.woff2') format('woff2');
  font-weight: 700;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-ExtraBoldItalic.woff2') format('woff2');
  font-weight: 800;
  font-style: italic;
  font-display: swap;
}
@font-face {
  font-family: 'Inter';
  src: url('./fonts/Inter/Inter-BlackItalic.woff2') format('woff2');
  font-weight: 900;
  font-style: italic;
  font-display: swap;
}

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

// Style variables and utilities from src/styles.
*,
*::before,
*::after {
  box-sizing: border-box;
}

html.prevent-scroll,
body.prevent-scroll {
  height: auto !important;
  padding-right: 17px;
  overflow-y: hidden !important;
}
html {
  overflow: hidden !important;
}

html,
body,
.v-application {
  height: 100%;
}

body {
  ::-webkit-scrollbar {
    width: 15px;
    height: 15px;
    background: none !important;
  }
  ::-webkit-scrollbar-track {
    background: none;
  }
  ::-webkit-scrollbar-thumb {
    background: var(--global-color-blue-grey-lighten-4);
    background-clip: padding-box;
    border: 4px solid rgba(0, 0, 0, 0);
    border-radius: 99999px;
  }
}

.v-select-list {
  margin-top: 10px !important;
  ::-webkit-scrollbar-track {
    background: #fff !important;
  }
}
#app {
  @extend %typography-small;
  height: 100%;
}

// ===
// Base element styles
// ===

h1 {
  @extend %typography-xxlarge;
}

h2 {
  @extend %typography-xlarge;
}

h3 {
  @extend %typography-large;
}

h4 {
  @extend %typography-medium;
}

h5,
h6 {
  @extend %typography-small;
}

// override vuetify default style globally

.v-application .text-h1.text-h1,
.v-application .text-h2.text-h2,
.v-application .text-h3.text-h3,
.v-application .text-h4.text-h4,
.v-application .text-h5.text-h5,
.v-application .text-h6.text-h6,
.v-application .text-subtitle-1.text-subtitle-1,
.v-application .text-subtitle-2.text-subtitle-2,
.v-application .subtitle-2.subtitle-2,
.v-application .text-body-1.text-body-1,
.v-application .text-body-2.text-body-2,
.v-application .text-button.text-button,
.v-application .text-caption.text-caption,
.v-application .text-overline.text-overline,
.v-application h2,
.v-application h3,
.v-application .caption {
  font-family: 'Inter', sans-serif !important;
}

.v-application .page-header .text-h5.text-h5 {
	font-family: 'Mont', sans-serif !important;
}

.v-navigation-drawer__content {
	font-family: 'Mont', sans-serif !important;
}

.v-navigation-drawer__content * {
	font-family: 'Mont', sans-serif !important;
}

.v-tooltip__content {
  max-width: 300px;
}

	// Form component font alignment - Inter font family with NextJS sizing

	// Radio buttons
	.v-radio {
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}
	}

	.v-radio-group {
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}
	}

	// Checkboxes
	.v-input--checkbox {
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}
	}

	// Switch toggles
	.v-input--switch {
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}
	}

	// Text fields
	.v-text-field {
		// Label
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}

		// Input text
		input {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Placeholder text
		input::placeholder {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Background colour - adapts to theme
		.v-input__slot {
			background-color: var(--global-card-background) !important;
		}
	}

	// Select/Dropdown fields
	.v-select {
		// Label
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}

		// Selected value display
		.v-select__selection {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Placeholder text
		input::placeholder {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Background colour - adapts to theme
		.v-input__slot {
			background-color: var(--global-card-background) !important;
		}
	}

	// Autocomplete fields (searchable dropdowns)
	.v-autocomplete {
		// Label
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}

		// Input text
		input {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Selected value display
		.v-select__selection {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Placeholder text
		input::placeholder {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Background colour - adapts to theme
		.v-input__slot {
			background-color: var(--global-card-background) !important;
		}
	}

	// Textarea fields
	.v-textarea {
		// Label font size - aligned with NextJS (text-sm = 0.875rem)
		.v-label {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 500;
		}

		// Content/input text font size - aligned with NextJS (text-sm = 0.875rem)
		.v-textarea__wrapper textarea {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
			font-weight: 400;
		}

		// Placeholder text font size
		.v-textarea__wrapper textarea::placeholder {
			font-size: 0.875rem !important;
			font-family: 'Inter', sans-serif;
		}

		// Background colour - adapts to theme
		.v-input__slot {
			background-color: var(--global-card-background) !important;
		}
	}

// Validation/error messages - aligned with NextJS (text-xs = 0.75rem)
.v-text-field .v-messages,
.v-select .v-messages,
.v-textarea .v-messages,
.v-input .v-messages {
	font-size: 0.75rem !important;
	font-family: 'Inter', sans-serif;
}

// Helper/hint text styling
.v-text-field .v-text-field__details,
.v-select .v-text-field__details,
.v-textarea .v-text-field__details,
.v-input .v-text-field__details {
	font-size: 0.75rem !important;
	font-family: 'Inter', sans-serif;
}

// Dropdown menu items styling
.v-select-list .v-list-item {
	.v-list-item__title {
		font-size: 0.875rem !important;
		font-family: 'Inter', sans-serif;
	}

	.v-list-item__subtitle {
		font-size: 0.75rem !important;
		font-family: 'Inter', sans-serif;
	}
}

// Tab content background - transparent to inherit from parent
.theme--light.v-tabs-items {
  width: 100%;
  background-color: rgba(0, 0, 0, 0) !important;
}

.theme--dark.v-tabs-items {
  width: 100%;
  background-color: transparent !important;
}

// Dark mode form field visibility fixes
.theme--dark {
  // Outlined field borders - ensure visibility
  .v-text-field--outlined fieldset,
  .v-select--outlined fieldset,
  .v-textarea--outlined fieldset {
    border-color: var(--global-muted-foreground) !important;
  }

  // Floating labels - ensure visibility
  .v-label {
    color: var(--global-muted-foreground) !important;
  }

  // Active/focused labels
  .v-label--active {
    color: var(--global-foreground) !important;
  }

  // Focused field borders
  .v-input--is-focused fieldset {
    border-color: var(--global-syrenis-black) !important;
  }

  // Focused labels
  .v-input--is-focused .v-label {
    color: var(--global-syrenis-black) !important;
  }
}

// Prevent floating label clipping on outlined fields
.v-text-field--outlined .v-input__slot,
.v-select--outlined .v-input__slot,
.v-textarea--outlined .v-input__slot {
  overflow: visible !important;
}

// ===
// Vendor
// ===

#nprogress .bar {
  background: var(--global-primary);
}

.container {
  overflow-y: auto;
}

// SystemCard component overrides
.system-card {
  box-shadow: 0 4px 6px -1px #0000001a, 0 2px 4px -2px #0000001a !important;
}

// SystemCard icon color override - only apply to the main system avatar icon
.system-card[style*="--icon-color"] .v-avatar .v-icon,
.system-card[style*="--icon-color"] .system-avatar .v-icon,
.cassie-system-card[style*="--icon-color"] .v-avatar .v-icon {
  color: var(--icon-color) !important;
}

</style>
